/**
 * The questions users would type themselves after a failure (18.08.2026).
 *
 * Each builder produces the prompt for one inline "Explain" affordance. They
 * live together because they share a rule that is easy to get wrong
 * per-call-site: attach enough context that the model does not need a
 * round trip to understand the problem, and no more than that — every
 * character here is billed to the user's own API key, and a 2 MB error page
 * helps nobody.
 *
 * They also all end by naming the tool that gets MORE context. That is
 * deliberate: it keeps the prompt short while telling the model exactly how
 * to go deeper if it needs to, instead of guessing from a truncated body.
 */

import { redactBody } from '../../utils/redactResponse';
import { scrubDbErrorValues } from './dbErrorScrub';

/** Clip a payload to something diagnosable but bounded. */
const clip = (text, max) => {
  const s = String(text ?? '');
  return s.length > max ? `${s.slice(0, max)}\n…(truncated)` : s;
};

const stringify = (value) => {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  try { return JSON.stringify(value, null, 2); } catch { return ''; }
};

/**
 * A request that returned 4xx/5xx, or failed to connect at all.
 *
 * `allowBody` is the response-privacy gate ("Let AI read responses", default
 * OFF — services/ai/responseAccess.js). Off: the body is not attached and the
 * prompt says so. On: the body is redacted BEFORE it is clipped, so a clip can
 * never cut a secret in half and leave a tail too short to match.
 */
export function buildRequestFailurePrompt({ response, method, url, name, allowBody = false } = {}) {
  if (!response) return '';

  const status = response.status === 0
    ? 'failed to connect (no HTTP status returned)'
    : `returned ${response.status}${response.statusText ? ` ${response.statusText}` : ''}`;

  const rawBody = stringify(response.data);
  const body = allowBody ? clip(redactBody(rawBody).body || '', 1500) : '';

  return [
    `My request "${name || 'Untitled'}" (${method || 'GET'} ${url || ''}) ${status}.`,
    'Explain the most likely cause and how to fix it.',
    'Call get_current_request if you need the headers, body or auth.',
    body ? `\nResponse body:\n${body}` : '',
    !allowBody && rawBody ? '\n(The response body is not included: "Let AI read responses" is off.)' : '',
  ].filter(Boolean).join(' ').trim();
}

/**
 * One or more failing assertions in the Tests tab.
 *
 * An assertion message embeds the ACTUAL value it compared — body data — so it
 * sits behind the same privacy gate as the body (`allowBody`, default OFF):
 * off, only the test names are sent. On, each message is redacted.
 */
export function buildTestFailurePrompt({ results, name, method, url, allowBody = false } = {}) {
  const failed = (results || []).filter((r) => !r.passed);
  if (failed.length === 0) return '';

  const detail = failed
    .slice(0, 10)
    .map((r) => (allowBody
      ? `- ${r.name}: ${clip(redactBody(String(r.error || 'assertion failed')).body, 300)}`
      : `- ${r.name}`))
    .join('\n');

  // name/method/url are optional: the Tests tab does not carry the request's
  // identity, and get_current_request gives the model a better answer than
  // three props threaded through two components for a nicer sentence.
  const which = name ? `"${name}" (${method || 'GET'} ${url || ''})` : 'the active request';

  return [
    `${failed.length} test${failed.length === 1 ? '' : 's'} failed on ${which}.`,
    'Call get_current_request to see which request this is.',
    'Explain why each one failed and whether the test or the API is wrong.',
    'Call get_last_response to read the actual body before answering.',
    `\n\nFailures:\n${detail}`,
    allowBody ? '' : '\n(Assertion messages are not included: "Let AI read responses" is off.)',
  ].filter(Boolean).join(' ').trim();
}

/**
 * A statement the database engine rejected. The error goes through the same
 * row-value scrub as a db_run_sql tool error: MySQL 1062 quotes the row value.
 */
export function buildSqlErrorPrompt({ error, sql, connectionName } = {}) {
  if (!error) return '';

  return [
    `This SQL failed${connectionName ? ` on "${connectionName}"` : ''}:`,
    `\n\n${clip(sql || '(statement unavailable)', 1200)}`,
    `\n\nThe database returned:\n${clip(scrubDbErrorValues(String(error), sql), 600)}`,
    '\n\nExplain what is wrong and give me a corrected statement.',
    'Call db_get_schema if you need the table or column names.',
  ].join('').trim();
}
